// wakeWord.js
// Arms "Hey Comprehend" the moment the panel mounts, using the existing
// Porcupine service (components/Misc/Synapse/WakeWordService). Degrades
// gracefully: if the key fetch or model init fails, push-to-talk still
// works and we just log it.
import WakeWordService from '../components/Misc/Synapse/WakeWordService';
import getDeviceData, { getDeviceFingerprint } from '../api/DeviceInfo';

const KEYWORD_MODEL_PATH = '/Hey-Comprehend_en_wasm_v3_0_0.ppn';

let service = null;
let arming = false;

// Synapse's audio cues: blip up = listening opened, blip down = closed.
const blipUp = typeof Audio !== 'undefined' ? new Audio('/blip.mp3') : null;
const blipDown =
  typeof Audio !== 'undefined' ? new Audio('/blip_down.mp3') : null;

export const playBlipUp = () => blipUp?.play().catch(() => {});
export const playBlipDown = () => blipDown?.play().catch(() => {});

/**
 * @param {Object} apiClient
 * @param {Function} onWake - called on every "Hey Comprehend" detection
 * @returns {Promise<boolean>} whether the wake word is armed
 */
export const armWakeWord = async (apiClient, onWake) => {
  if (service || arming) return Boolean(service);
  arming = true;
  try {
    // Picovoice bills per device — the backend requires a fingerprint
    // (same payload SynapseBrain sends).
    const deviceData = await getDeviceData();
    const keyResponse = await apiClient.getSynapseKey('picovoice_wakeword', {
      deviceFingerprint: getDeviceFingerprint(),
      deviceInfo: {
        osType: deviceData.osType,
        browser: deviceData.browser,
        userAgent: deviceData.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      },
    });
    if (!keyResponse?.accessKey) {
      throw new Error(
        `No Picovoice key available (backend said: ${JSON.stringify(
          keyResponse ?? null,
        )})`,
      );
    }
    const instance = new WakeWordService({
      accessKey: keyResponse.accessKey,
      keywordModel: KEYWORD_MODEL_PATH,
      onWakeWord: () => onWake(),
      // Silence handling is owned by the runtime's listening window,
      // not the wake-word service.
      silenceCallback: null,
      silenceTimeout: 60 * 60 * 1000,
    });
    await instance.init();
    service = instance;
    console.info('ComprehendLive: wake word armed');
    return true;
  } catch (error) {
    console.warn('ComprehendLive: wake word unavailable, PTT only', error);
    return false;
  } finally {
    arming = false;
  }
};

export const disarmWakeWord = async () => {
  if (!service) return;
  const instance = service;
  service = null;
  try {
    await instance.stop();
  } catch (error) {
    console.warn('ComprehendLive: wake word stop failed', error);
  }
};

export const wakeWordArmed = () => Boolean(service);
